import React, { useState, useEffect } from 'react';
import { getPayments, createPayment, getBookings, getCustomers, getInvoices, generateInvoice, downloadInvoicePdf, generateReceipt } from '../lib/api';
import { formatCurrency, CURRENCY_SYMBOL } from '../lib/currency';
import { PAYMENTS } from '../constants/testIds';
import { DateFilter } from '../components/DateFilter';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ExportImportButtons } from '../components/ExportImportButtons';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, FileText, Loader2, CreditCard, Receipt, Download, Printer } from 'lucide-react';
import { toast } from 'sonner';

const paymentMethods = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
];

const getStatusClass = (status) => {
  const classes = {
    pending: 'bg-amber-50 text-amber-700',
    paid: 'bg-emerald-50 text-emerald-700',
    partial: 'bg-blue-50 text-blue-700',
    refunded: 'bg-slate-100 text-slate-700',
  };
  return classes[status] || 'bg-slate-100 text-slate-700';
};

export const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [formData, setFormData] = useState({
    booking_id: '',
    customer_id: '',
    amount: '',
    payment_method: 'bank_transfer',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (filterMonth) params.month = filterMonth;
      const [paymentsRes, invoicesRes, bookingsRes, customersRes] = await Promise.all([
        getPayments(params),
        getInvoices(params),
        getBookings(),
        getCustomers(),
      ]);
      setPayments(paymentsRes.data);
      setInvoices(invoicesRes.data);
      setBookings(bookingsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = () => loadData();
  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setFilterMonth('');
    loadData();
  };

  const resetForm = () => {
    setFormData({
      booking_id: '',
      customer_id: '',
      amount: '',
      payment_method: 'bank_transfer',
      notes: '',
    });
  };

  const handleBookingSelect = (bookingId) => {
    const booking = bookings.find(b => b.id === bookingId);
    setFormData({
      ...formData,
      booking_id: bookingId,
      customer_id: booking?.customer_id || '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await createPayment({
        ...formData,
        amount: parseFloat(formData.amount) || 0,
      });
      toast.success('Payment recorded successfully');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateInvoice = async (bookingId) => {
    try {
      await generateInvoice(bookingId);
      toast.success('Invoice generated successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    }
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await downloadInvoicePdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Unknown';
  };

  const getBookingInfo = (bookingId) => {
    const booking = bookings.find(b => b.id === bookingId);
    return booking ? `${booking.destination} - ${getCustomerName(booking.customer_id)}` : 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid={PAYMENTS.container} className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight" style={{ color: '#84bd0e' }}>Payments & Invoicing</h1>
          <p className="text-muted-foreground mt-1">Track payments and manage invoices</p>
        </div>
        <div className="flex items-center gap-3">
          <DateFilter
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            month={filterMonth}
            setMonth={setFilterMonth}
            onApply={handleDateFilter}
            onClear={clearDateFilter}
          />
          <ExportImportButtons module="payments" onImportSuccess={loadData} />
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid={PAYMENTS.addBtn} onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />
              Record Payment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-outfit">Record Payment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Booking *</Label>
                <Select value={formData.booking_id} onValueChange={handleBookingSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select booking" />
                  </SelectTrigger>
                  <SelectContent>
                    {bookings.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.destination} - {getCustomerName(b.customer_id)} ({formatCurrency(b.total_amount)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount ({CURRENCY_SYMBOL}) *</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentMethods.map((m) => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={submitting}>
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Record Payment
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Tabs defaultValue="payments" className="w-full">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="payments" className="data-[state=active]:bg-white">
            <CreditCard className="w-4 h-4 mr-2" />
            Payments
          </TabsTrigger>
          <TabsTrigger value="invoices" className="data-[state=active]:bg-white">
            <Receipt className="w-4 h-4 mr-2" />
            Invoices
          </TabsTrigger>
        </TabsList>

        <TabsContent value="payments" className="mt-6">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <Table data-testid={PAYMENTS.table}>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Booking</TableHead>
                    <TableHead className="font-semibold">Customer</TableHead>
                    <TableHead className="font-semibold">Method</TableHead>
                    <TableHead className="font-semibold text-right">Amount</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-center">Receipt</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.length > 0 ? (
                    payments.map((p) => (
                      <TableRow key={p.id} className="hover:bg-slate-50">
                        <TableCell className="text-sm">
                          {new Date(p.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{getBookingInfo(p.booking_id)}</TableCell>
                        <TableCell>{getCustomerName(p.customer_id)}</TableCell>
                        <TableCell className="capitalize">{p.payment_method.replace('_', ' ')}</TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(p.amount)}</TableCell>
                        <TableCell>
                          <Badge className={getStatusClass(p.status)}>{p.status}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              try {
                                const { data } = await generateReceipt(p.id);
                                // Open receipt in new window
                                const receiptUrl = `${process.env.REACT_APP_BACKEND_URL}/api/receipts/${data.id}/pdf`;
                                window.open(receiptUrl, '_blank');
                                toast.success('Receipt generated');
                              } catch (error) {
                                toast.error('Failed to generate receipt');
                              }
                            }}
                            title="Print Receipt"
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        No payments found. Click "Record Payment" to add one.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoices" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-200">
              <div className="flex items-center justify-between">
                <CardTitle className="font-outfit text-lg">Invoices</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Invoice #</TableHead>
                    <TableHead className="font-semibold">Customer</TableHead>
                    <TableHead className="font-semibold">Destination</TableHead>
                    <TableHead className="font-semibold text-right">Total</TableHead>
                    <TableHead className="font-semibold text-right">Paid</TableHead>
                    <TableHead className="font-semibold text-right">Due</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.length > 0 ? (
                    invoices.map((inv) => (
                      <TableRow key={inv.id} className="hover:bg-slate-50">
                        <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                        <TableCell>{inv.customer_name}</TableCell>
                        <TableCell>{inv.destination}</TableCell>
                        <TableCell className="text-right">{formatCurrency(inv.total_amount)}</TableCell>
                        <TableCell className="text-right text-emerald-600">{formatCurrency(inv.paid_amount)}</TableCell>
                        <TableCell className="text-right text-amber-600">{formatCurrency(inv.due_amount)}</TableCell>
                        <TableCell>
                          <Badge className={getStatusClass(inv.status)}>{inv.status}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadPdf(inv.id, inv.invoice_number)}
                            data-testid={`download-invoice-${inv.id}`}
                            className="text-xs"
                          >
                            <Download className="w-3 h-3 mr-1" />
                            PDF
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        No invoices found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Bookings without invoices */}
          <Card className="border-slate-200 mt-6">
            <CardHeader className="border-b border-slate-200">
              <CardTitle className="font-outfit text-lg">Generate Invoice</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500 mb-4">Select a booking to generate an invoice:</p>
              <div className="space-y-2">
                {bookings.filter(b => !invoices.some(i => i.booking_id === b.id)).map((b) => (
                  <div key={b.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">{b.destination}</p>
                      <p className="text-sm text-slate-500">{getCustomerName(b.customer_id)} - {formatCurrency(b.total_amount)}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleGenerateInvoice(b.id)}>
                      <FileText className="w-4 h-4 mr-2" />
                      Generate
                    </Button>
                  </div>
                ))}
                {bookings.filter(b => !invoices.some(i => i.booking_id === b.id)).length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">All bookings have invoices generated.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
